/**
 * useUpkeepMonitoring Hook
 * 
 * Custom React hook for Upkeep Budget Monitoring data management and state
 */

import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import UpkeepMonitoringApiService from '../services/upkeepMonitoringApiService';
import {
    UpkeepBudgetDataRaw,
    UpkeepMaterialDataRaw,
    UpkeepBudgetRow,
    UpkeepBudgetFilters,
} from '../types';
import { transformToDisplayRows } from '../utils';
import { UPKEEP_MONITORING_DEFAULTS } from '../apiConfig';

/**
 * Hook return type
 */
interface UseUpkeepMonitoringReturn {
    rawUpkeep: UpkeepBudgetDataRaw[];
    rawMaterial: UpkeepMaterialDataRaw[];
    displayRows: UpkeepBudgetRow[];
    loading: boolean;
    error: string | null;
    filters: UpkeepBudgetFilters;
    setFilters: (filters: UpkeepBudgetFilters) => void;
    fetchData: (filtersToUse?: UpkeepBudgetFilters) => Promise<void>;
    totalCount: number;
}

/**
 * Get default filters with current month and year
 */
const getDefaultFilters = (): UpkeepBudgetFilters => {
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = String(now.getMonth() + 1).padStart(2, '0');

    return {
        revision: UPKEEP_MONITORING_DEFAULTS.REVISION,
        budget_year: year,
        budget_month: month,
        company_id: UPKEEP_MONITORING_DEFAULTS.COMPANY_ID,
    };
};

/**
 * Custom hook for managing Upkeep Monitoring data and state
 */
export const useUpkeepMonitoring = (): UseUpkeepMonitoringReturn => {
    const toast = useToast();

    const [rawUpkeep, setRawUpkeep] = useState<UpkeepBudgetDataRaw[]>([]);
    const [rawMaterial, setRawMaterial] = useState<UpkeepMaterialDataRaw[]>([]);
    const [displayRows, setDisplayRows] = useState<UpkeepBudgetRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<UpkeepBudgetFilters>(getDefaultFilters());
    const [totalCount, setTotalCount] = useState<number>(0);

    /**
     * Fetch Upkeep Budget data from API
     * @param filtersToUse - Optional filters to use, defaults to current state filters
     */
    const fetchData = useCallback(async (filtersToUse?: UpkeepBudgetFilters) => {
        setLoading(true);
        setError(null);

        const activeFilters = filtersToUse || filters;

        try {
            const response = await UpkeepMonitoringApiService.getUpkeepBudgetReport(activeFilters);

            if (response.status === 'success') {
                setRawUpkeep(response.data.upkeep);
                setRawMaterial(response.data.material);
                setDisplayRows(transformToDisplayRows(response.data.upkeep, response.data.material));
                setTotalCount(response.count.upkeep + response.count.material);
            } else {
                throw new Error('Failed to fetch upkeep budget data');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Gagal mengambil data budget upkeep';
            setError(errorMessage);
            setRawUpkeep([]);
            setRawMaterial([]);
            setDisplayRows([]);
            setTotalCount(0);

            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setLoading(false);
        }
    }, [filters, toast]);

    return {
        rawUpkeep,
        rawMaterial,
        displayRows,
        loading,
        error,
        filters,
        setFilters,
        fetchData,
        totalCount,
    };
};

export default useUpkeepMonitoring;
